import checkFF from '@/lib/feature-flag';
import { DateTime } from 'luxon';
import { AddOn } from '@apps-once/types/checkout.d';

/**
 * Validates if an add-on should be available based on its feature flag
 *
 * This utility handles different types of feature flags:
 * - No feature flag: Add-on is available
 * - String feature flag: Treated as a date, checks if it's been published
 * - Non-string feature flag: Uses standard feature flag evaluation
 *
 * @param {AddOn} addOn - The add-on object
 * @param {function(string): (boolean|string)} getFeatureFlag - Optional function to get feature flag value
 * @return {boolean} Whether the add-on should be available
 *
 * @example
 * // With date-based feature flag
 * const addOn = { featureFlagPreSubscribe: 'some_date_flag' };
 * const isAvailable = isAddOnAvailableByPreSubscribeFeatureFlag(addOn);
 *
 * @example
 * // With boolean feature flag and custom getter
 * const addOn = { featureFlagPreSubscribe: 'some_flag' };
 * const isAvailable = isAddOnAvailableByPreSubscribeFeatureFlag(addOn, (flag) => customCheck(flag));
 */
export const isAddOnAvailableByPreSubscribeFeatureFlag = (
  addOn: AddOn,
  getFeatureFlag: (flag: string) => boolean | string = checkFF
): boolean => {
  const featureFlag = addOn.featureFlagPreSubscribe;

  // If no feature flag is set, add-on is available
  if (!featureFlag) {
    return true;
  }

  const flagValue = getFeatureFlag(featureFlag);

  // If the feature flag is a string, treat it as a date and check if it's published
  if (typeof flagValue === 'string') {
    try {
      // If flagValue is a string (date), parse it and check if published
      const publishDate = DateTime.fromISO(flagValue);
      if (publishDate.isValid) {
        // Check if the publish date has passed (is <= 0 days from now)
        return publishDate.diffNow('days').days <= 0;
      }
    } catch (error) {
      // If date parsing fails, fall back to standard feature flag check
      console.warn(
        `Failed to parse date from feature flag ${featureFlag}:`,
        error
      );
      return Boolean(flagValue);
    }
  }

  return Boolean(flagValue);
};

/**
 * Checks if a date-based feature flag represents a future date (not yet published)
 *
 * @param {string} featureFlagKey - The feature flag key to check
 * @param {function(string): (boolean|string)} getFeatureFlag - Function to get feature flag value
 * @return {boolean} Whether the feature flag date is in the future
 */
export const isFeatureFlagDateInFuture = (
  featureFlagKey: string,
  getFeatureFlag: (flag: string) => boolean | string = checkFF
): boolean => {
  if (!featureFlagKey) {
    return false;
  }

  try {
    const flagValue = getFeatureFlag(featureFlagKey);

    if (typeof flagValue === 'string') {
      const publishDate = DateTime.fromISO(flagValue);
      if (publishDate.isValid) {
        // Check if the publish date is in the future (> 0 days from now)
        return publishDate.diffNow('days').days > 0;
      }
    }

    return false;
  } catch (error) {
    console.warn(
      `Failed to parse date from feature flag ${featureFlagKey}:`,
      error
    );
    return false;
  }
};

/**
 * Gets the date value from a feature flag
 *
 * @param {string} featureFlagKey - The feature flag key to get date from
 * @param {function(string): (boolean|string)} getFeatureFlag - Function to get feature flag value
 * @return {Date|null} The date from the feature flag, or null if not available
 */
export const getFeatureFlagDate = (
  featureFlagKey: string,
  getFeatureFlag: (flag: string) => boolean | string = checkFF
): Date | null => {
  if (!featureFlagKey) {
    return null;
  }

  try {
    const flagValue = getFeatureFlag(featureFlagKey);

    if (typeof flagValue === 'string') {
      const parsedDate = DateTime.fromISO(flagValue);
      if (parsedDate.isValid) {
        return parsedDate.toJSDate();
      }
    }

    return null;
  } catch (error) {
    console.warn(
      `Failed to get date from feature flag ${featureFlagKey}:`,
      error
    );
    return null;
  }
};

/**
 * Calculates the difference in days between a feature flag date and current time
 *
 * @param {string} featureFlagKey - The feature flag key to check
 * @param {function(string): (boolean|string)} getFeatureFlag - Function to get feature flag value
 * @return {number} Number of days between feature flag date and now (positive = future, negative = past)
 */
export const getFeatureFlagDateDiffInDays = (
  featureFlagKey: string,
  getFeatureFlag: (flag: string) => boolean | string = checkFF
): number => {
  if (!featureFlagKey) {
    return 0;
  }

  try {
    const flagValue = getFeatureFlag(featureFlagKey);

    if (typeof flagValue === 'string') {
      const publishDate = DateTime.fromISO(flagValue);
      if (publishDate.isValid) {
        return publishDate.diffNow('days').days;
      }
    }

    return 0;
  } catch (error) {
    console.warn(
      `Failed to calculate date diff from feature flag ${featureFlagKey}:`,
      error
    );
    return 0;
  }
};
